import { useCallback, useEffect, useMemo, useState } from 'react'
import { AppLayout } from '../components/AppLayout'
import { sellerApi, type SellerLine, type RejectReason, type LineDecisionOutcome } from '../api/endpoints'
import { formatPrice, formatCarat, formatDate } from '../lib/format'

/**
 * /seller-inbox — supplier-facing approve/reject inbox (Brief A).
 *
 * Mirrors Heera's /Admin/SupplierConfirmStones experience:
 *   - per-row Approve / Reject buttons
 *   - bulk Approve all / Reject all when every visible row is pending
 *   - Reject opens a reason picker modal (multi-checkbox + free text);
 *     reason is submitted alongside the decision via /api/seller/line-decisions.
 *   - After submit: inline inventory auto-prompt cards (THE-BIG-PLAN §2.4)
 *     match each rejection to a follow-up CTA — Mark unavailable / Update
 *     price / Remove from listing / Edit specs.
 *
 * Surfaced through the standard AppLayout chrome so it inherits the
 * same brand styling as the rest of the platform UI.
 */
export function SellerInbox() {
  const [lines, setLines] = useState<SellerLine[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [rejectModal, setRejectModal] = useState<{ ids: number[]; stockIds: string[] } | null>(null)
  const [reasons, setReasons] = useState<RejectReason[]>([])
  const [outcomes, setOutcomes] = useState<LineDecisionOutcome[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await sellerApi.pending('pending')
      setLines(r.lines || [])
      setError(null)
    } catch (e: unknown) {
      const err = e as { status?: number; message?: string }
      if (err.status === 403) setError('Supplier or admin access required.')
      else setError(err.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { load() }, [load])

  // Reason list cached after first fetch — rarely changes.
  useEffect(() => {
    sellerApi.rejectReasons().then((r) => setReasons(r.reasons || [])).catch(() => {
      // Local fallback so the modal still renders if endpoint is offline.
      setReasons([
        { code: 1, label: 'Sold elsewhere',         displayOrder: 10 },
        { code: 2, label: 'Price changed',          displayOrder: 20 },
        { code: 3, label: 'Quality or cert issue',  displayOrder: 30 },
        { code: 4, label: 'Stone damaged',          displayOrder: 40 },
        { code: 5, label: 'Mis-listed (specs wrong)', displayOrder: 50 },
        { code: 6, label: 'Other',                  displayOrder: 99 },
      ])
    })
  }, [])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }

  async function approve(id: number) {
    setBusyId(id)
    try {
      const r = await sellerApi.lineDecisions([{ orderDetailId: id, status: 'approve' }])
      setLines((prev) => prev.filter((l) => l.orderDetailId !== id))
      setOutcomes(r.outcomes || [])
      showToast('Stone approved')
    } catch {
      showToast('Could not approve — try again')
    } finally {
      setBusyId(null)
    }
  }

  function openReject(line: SellerLine) {
    setRejectModal({ ids: [line.orderDetailId], stockIds: [line.stockId] })
  }

  async function approveAll() {
    if (lines.length === 0) return
    if (!window.confirm(`Approve all ${lines.length} pending stone${lines.length !== 1 ? 's' : ''}?`)) return
    setBulkBusy(true)
    try {
      const r = await sellerApi.lineDecisions(
        lines.map((l) => ({ orderDetailId: l.orderDetailId, status: 'approve' as const }))
      )
      setLines([])
      setOutcomes(r.outcomes || [])
      showToast(`Approved ${lines.length}`)
    } catch {
      showToast('Bulk approve failed')
    } finally {
      setBulkBusy(false)
    }
  }

  function openRejectAll() {
    if (lines.length === 0) return
    setRejectModal({
      ids: lines.map((l) => l.orderDetailId),
      stockIds: lines.map((l) => l.stockId),
    })
  }

  async function submitReject(reasonCode: number, reasonText: string) {
    if (!rejectModal) return
    setBulkBusy(true)
    try {
      const r = await sellerApi.lineDecisions(
        rejectModal.ids.map((id) => ({
          orderDetailId: id,
          status: 'reject' as const,
          reasonCode,
          reasonText: reasonText || undefined,
        }))
      )
      setLines((prev) => prev.filter((l) => !rejectModal.ids.includes(l.orderDetailId)))
      setOutcomes(r.outcomes || [])
      showToast(rejectModal.ids.length > 1
        ? `Rejected ${rejectModal.ids.length} stones — buyer notified`
        : 'Stone rejected — buyer notified'
      )
      setRejectModal(null)
    } catch {
      showToast('Could not submit — try again')
    } finally {
      setBulkBusy(false)
    }
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-3xl px-6 py-10 space-y-4">
        <header>
          <h1 className="text-2xl font-bold text-ink tracking-tight">Stone requests</h1>
          <p className="text-xs text-soft mt-1">Approve or reject incoming buyer requests for your stones.</p>
        </header>

        {/* Bulk actions — visible when there are 2+ pending rows.
            Approve-all is one-click; reject-all opens the reason modal. */}
        {!loading && !error && lines.length > 1 && (
          <div className="flex items-center gap-2" data-testid="bulk-actions">
            <button
              type="button"
              disabled={bulkBusy}
              onClick={approveAll}
              className="flex-1 h-10 rounded-full bg-primary/10 text-primary text-[13px] font-semibold hover:bg-primary/15 disabled:opacity-50"
              data-testid="approve-all"
            >
              {bulkBusy ? '…' : `Approve all (${lines.length})`}
            </button>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={openRejectAll}
              className="h-10 px-4 rounded-full border border-danger/30 text-danger text-[13px] font-semibold hover:bg-danger/5 disabled:opacity-50"
              data-testid="reject-all"
            >
              Reject all
            </button>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted py-10 text-center">Loading…</p>
        ) : error ? (
          <p className="text-sm text-danger py-10 text-center">{error}</p>
        ) : lines.length === 0 ? (
          <div className="card text-center py-10">
            <p className="text-sm text-muted">You're caught up. No pending requests.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {lines.map((l) => (
              <li key={l.orderDetailId} className="card flex items-start justify-between gap-4" data-testid="inbox-row">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink">#{l.stockId}</p>
                  <p className="text-xs text-soft">
                    Order #{l.orderId} · {formatCarat(l.carat)} · {formatDate(l.orderDate)}
                  </p>
                </div>
                <p className="text-sm font-bold text-ink w-28 text-right">{formatPrice(l.amount)}</p>
                <div className="flex flex-col gap-2 w-32">
                  <button
                    type="button"
                    disabled={busyId === l.orderDetailId}
                    onClick={() => approve(l.orderDetailId)}
                    className="h-9 rounded-md bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                    data-testid="approve"
                    data-order-detail-id={l.orderDetailId}
                  >
                    {busyId === l.orderDetailId ? '…' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === l.orderDetailId}
                    onClick={() => openReject(l)}
                    className="h-9 rounded-md border border-danger/40 text-danger text-sm font-semibold hover:bg-danger/5 disabled:opacity-50"
                    data-testid="reject"
                    data-order-detail-id={l.orderDetailId}
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {outcomes.filter((o) => o.status === 'rejected' && o.allowed).length > 0 && (
          <InventoryAutoPrompt outcomes={outcomes} onDismiss={(id) => setOutcomes((prev) => prev.filter((o) => o.orderDetailId !== id))} />
        )}
      </div>

      {rejectModal && (
        <RejectModal
          ids={rejectModal.ids}
          stockIds={rejectModal.stockIds}
          reasons={reasons}
          submitting={bulkBusy}
          onCancel={() => setRejectModal(null)}
          onSubmit={submitReject}
        />
      )}

      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-8 z-30 bg-navy text-white text-xs font-medium px-4 py-2 rounded-md shadow-card"
          data-testid="inbox-toast"
          role="status"
        >
          {toast}
        </div>
      )}
    </AppLayout>
  )
}

function RejectModal({
  ids, stockIds, reasons, submitting, onCancel, onSubmit,
}: {
  ids: number[]
  stockIds: string[]
  reasons: RejectReason[]
  submitting: boolean
  onCancel: () => void
  onSubmit: (reasonCode: number, reasonText: string) => void
}) {
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [freeText, setFreeText] = useState('')
  const [optional, setOptional] = useState('')
  const showOther = picked.has(6)
  const title = ids.length > 1
    ? `Reason for rejecting ${ids.length} stones`
    : `Reason for rejecting #${stockIds[0] || ids[0]}`

  function toggle(code: number) {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  // Escape dismisses (keyboard parity with every other dialog); guarded so
  // an in-flight submit can't be orphaned mid-request.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [submitting, onCancel])

  function submit() {
    if (picked.size === 0) { alert('Pick at least one reason.'); return }
    if (showOther && !freeText.trim()) { alert('Tell the buyer more — required for Other.'); return }

    // Persist single reasonCode (FK in DB schema) but include every
    // selected label in the free-text so the buyer sees them all.
    const codes = Array.from(picked).sort()
    const labels = codes.map((c) => reasons.find((r) => r.code === c)?.label || '').filter(Boolean)
    const reasonCode = codes[0]
    let combinedText = labels.length > 1 ? labels.join('; ') : ''
    if (freeText.trim()) combinedText = combinedText ? `${combinedText} — ${freeText.trim()}` : freeText.trim()
    if (optional.trim()) combinedText = combinedText ? `${combinedText} | ${optional.trim()}` : optional.trim()
    onSubmit(reasonCode, combinedText)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4"
      role="dialog"
      aria-modal="true"
      data-testid="reject-modal"
      onClick={() => { if (!submitting) onCancel() }}
    >
      {/* shadow-pop was never a defined token — the dialog floated with no
          elevation. shadow-elev-4 is the modal tier of the v2 ladder. */}
      <div className="bg-paper rounded-card shadow-elev-4 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-bold text-ink mb-1">{title}</h2>
        <p className="text-xs text-soft mb-4">Buyers see the reason on their order detail.</p>

        <div className="space-y-2 mb-3">
          {reasons.map((r) => (
            <label key={r.code} className="flex items-center gap-2 cursor-pointer text-sm text-ink">
              <input
                type="checkbox"
                checked={picked.has(r.code)}
                onChange={() => toggle(r.code)}
                data-testid={`reason-${r.code}`}
              />
              <span>{r.label}</span>
            </label>
          ))}
        </div>

        {showOther && (
          <div className="mb-3">
            <label className="block text-xs font-semibold text-ink mb-1">
              Tell the buyer more <span className="text-danger">*</span>
            </label>
            <textarea
              className="input"
              rows={3}
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              placeholder="Required for Other"
              data-testid="reject-free-text"
            />
          </div>
        )}

        <div className="mb-4">
          <label className="block text-xs font-semibold text-ink mb-1">Optional note for the buyer</label>
          <textarea
            className="input"
            rows={2}
            value={optional}
            onChange={(e) => setOptional(e.target.value)}
            placeholder="Adds context to the email the buyer receives"
          />
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCancel} className="btn-secondary text-sm" disabled={submitting}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="h-10 px-4 rounded-md bg-danger text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            data-testid="reject-submit"
          >
            {submitting ? '…' : `Reject stone${ids.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Inventory auto-prompt — THE-BIG-PLAN §2.4. Closes the loop after a
 * rejection by surfacing a reason-matched CTA so the supplier doesn't
 * have to navigate elsewhere to keep their inventory current.
 */
function InventoryAutoPrompt({
  outcomes, onDismiss,
}: {
  outcomes: LineDecisionOutcome[]
  onDismiss: (orderDetailId: number) => void
}) {
  const rejected = useMemo(
    () => outcomes.filter((o) => o.status === 'rejected' && o.allowed),
    [outcomes]
  )
  if (rejected.length === 0) return null

  function ctaFor(reasonCode: number | undefined): { prompt: string; label: string } {
    switch (reasonCode) {
      case 1: return { prompt: 'Mark this stone as unavailable in your inventory?', label: 'Mark unavailable' }
      case 2: return { prompt: 'Update the price on this stone?', label: 'Update price' }
      case 3:
      case 4: return { prompt: 'Remove this stone from your inventory on DiaEx?', label: 'Remove stone' }
      case 5: return { prompt: 'Edit the specs for this stone?', label: 'Open editor' }
      default: return { prompt: 'Anything to update on this stone?', label: 'Update' }
    }
  }

  async function handle(o: LineDecisionOutcome) {
    if (o.reasonCode === 5) {
      window.open('/seller/mystock', '_blank')
      onDismiss(o.orderDetailId)
      return
    }
    // Brief D — wire the auto-prompt to /api/seller/mark-unavailable.
    // Reason codes 1 (sold elsewhere) / 3 (cert issue) / 4 (damaged)
    // all map to "stop offering this stone" semantically.
    if (o.stockId && (o.reasonCode === 1 || o.reasonCode === 3 || o.reasonCode === 4)) {
      try {
        await sellerApi.markUnavailable([o.stockId], `auto-prompt:reason=${o.reasonCode}`)
      } catch {
        // Failed write doesn't break the UX — supplier can still
        // act manually. Don't surface a flash error here.
      }
    }
    // Reason 2 (price changed) is intentionally not auto-applied —
    // it needs the new price, which the auto-prompt doesn't capture.
    // Tej's spec: redirect to /seller/mystock for an explicit edit.
    if (o.reasonCode === 2) {
      window.open('/seller/mystock', '_blank')
    }
    onDismiss(o.orderDetailId)
  }

  return (
    <section className="card border-warn/30 bg-warn/5" data-testid="auto-prompt">
      <p className="text-sm font-semibold text-ink mb-1">Update your inventory while you're here</p>
      <p className="text-xs text-soft mb-3">A quick action matched to each reason. One click closes the loop.</p>
      <ul className="divide-y divide-border">
        {rejected.map((o) => {
          const { prompt, label } = ctaFor(o.reasonCode)
          return (
            <li key={o.orderDetailId} className="py-2 flex items-center justify-between gap-3" data-prompt-id={o.orderDetailId}>
              <p className="text-sm text-ink">
                <strong>#{o.stockId || o.orderDetailId}</strong> — {prompt}
              </p>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => handle(o)}
                  className="h-8 px-3 rounded-md bg-primary text-white text-xs font-semibold hover:opacity-90"
                  data-testid="auto-prompt-action"
                >
                  {label}
                </button>
                <button
                  type="button"
                  onClick={() => onDismiss(o.orderDetailId)}
                  className="h-8 px-3 rounded-md border border-border text-xs text-soft font-medium hover:bg-page"
                  data-testid="auto-prompt-skip"
                >
                  Skip
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
